import React, { ReactElement } from 'react';
import { StyleSheet, View } from 'react-native';
import { WH, WW } from '_utils';
import { screenMargin, smallestMargin } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { HeaderWithNav } from 'Components';
import SkeletonLoader from 'Components/SkeletonLoader';

const RequestDetailSkeletonLoader = (): ReactElement => {
    const { theme } = useThemeContext();
    return (
        <SkeletonLoader variant="provider" style={{ flex: 1, backgroundColor: theme.mainBackground }}>
            <HeaderWithNav
                title={
                    <SkeletonLoader variant="foreground" style={{ width: WW * 0.3, height: WH * 0.035 }} index={0} />
                }
                action={
                    <SkeletonLoader
                        variant="foreground"
                        style={{ width: WW * 0.1, height: WH * 0.035, marginRight: screenMargin }}
                        index={0}
                    />
                }
            />
            <View style={{ padding: screenMargin }}>
                {/* Grouping SkeletonLoaders with index 1 */}
                <SkeletonLoader variant="container" style={styles.title} index={1}>
                    <SkeletonLoader variant="foreground" style={styles.title} index={1} />
                    <View style={styles.subContainer}>
                        <SkeletonLoader variant="foreground" style={styles.timestamp} index={1} />
                        <SkeletonLoader variant="foreground" style={styles.subscribeButton} index={1} />
                    </View>
                </SkeletonLoader>

                {/* Grouping SkeletonLoaders with index 2 */}
                <SkeletonLoader variant="container" style={styles.content} index={2}>
                    <SkeletonLoader variant="foreground" style={styles.contentLine} />
                    <SkeletonLoader variant="foreground" style={styles.contentLine} />
                    <SkeletonLoader variant="foreground" style={[styles.contentLine, styles.shortLine]} />
                </SkeletonLoader>

                {/* Grouping SkeletonLoaders with index 3 */}
                <SkeletonLoader variant="container" index={3}>
                    <SkeletonLoader variant="foreground" style={styles.attachmentTitle} />
                    <View style={styles.attachments}>
                        <SkeletonLoader variant="foreground" style={styles.attachment} />
                        <SkeletonLoader variant="foreground" style={styles.attachment} />
                    </View>
                </SkeletonLoader>

                {/* Grouping SkeletonLoaders with index 4 and 5 */}
                <SkeletonLoader variant="container" index={4}>
                    <SkeletonLoader variant="foreground" style={styles.byText} />
                    <View style={styles.creatorInfo}>
                        <SkeletonLoader variant="foreground" style={styles.avatar} />
                        <View style={styles.creatorDetails}>
                            <SkeletonLoader variant="foreground" style={styles.creatorName} index={5} />
                            <SkeletonLoader variant="foreground" style={styles.creatorApartment} index={5} />
                        </View>
                    </View>
                </SkeletonLoader>
            </View>
        </SkeletonLoader>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    apartmentGroups: {
        flexDirection: 'row',
        marginBottom: smallestMargin,
        gap: smallestMargin,
    },
    apartmentChip: {
        height: WH * 0.03,
        width: WW * 0.2,
        borderRadius: WW * 0.015,
        overflow: 'hidden',
    },
    title: {
        height: WH * 0.04,
        width: '90%',
        marginBottom: smallestMargin,
        overflow: 'hidden',
    },
    subContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: smallestMargin * 2,
    },
    timestamp: {
        height: WH * 0.02,
        width: WW * 0.2,
        overflow: 'hidden',
    },
    subscribeButton: {
        height: WH * 0.035,
        width: WW * 0.25,
        borderRadius: WW * 0.02,
        overflow: 'hidden',
    },
    content: {
        marginBottom: smallestMargin * 2,
        gap: smallestMargin,
    },
    contentLine: {
        height: WH * 0.02,
        width: '100%',
        overflow: 'hidden',
    },
    shortLine: {
        width: '60%',
    },
    attachmentTitle: {
        height: WH * 0.025,
        width: WW * 0.3,
        marginBottom: smallestMargin,
        overflow: 'hidden',
    },
    attachments: {
        flexDirection: 'row',
        gap: smallestMargin,
        marginBottom: smallestMargin * 2,
    },
    attachment: {
        height: WH * 0.06,
        width: WH * 0.06,
        borderRadius: 8,
        overflow: 'hidden',
    },
    byText: {
        height: WH * 0.02,
        width: WW * 0.15,
        marginBottom: smallestMargin,
        overflow: 'hidden',
    },
    creatorInfo: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    avatar: {
        width: WW * 0.12,
        height: WW * 0.12,
        borderRadius: WW * 0.06,
        overflow: 'hidden',
    },
    creatorDetails: {
        marginLeft: smallestMargin,
        gap: smallestMargin / 2,
    },
    creatorName: {
        height: WH * 0.02,
        width: WW * 0.3,
        overflow: 'hidden',
    },
    creatorApartment: {
        height: WH * 0.015,
        width: WW * 0.2,
        overflow: 'hidden',
    },
});

export default RequestDetailSkeletonLoader;
